import { useEffect } from 'react';

import { RowData } from '@tanstack/react-table';

import { TableDebugger } from './Debugger/Debugger';
import { useTableContext } from './table.context';
import type { TableDataProvider } from './types/types';

// In controlled mode using this component allows the table data to be driving from props.

// Turn this into a function component and use the useEffect hook to update the table data.
const TableDataProviderComponent = <TData extends RowData>({
  data,
  meta,
  debug = true,
  columns,
  isLoading,
  exportOptions,
  pdfOptions,
}: TableDataProvider<TData>) => {
  const ctx = useTableContext();

  useEffect(() => {
    data && ctx.setData?.(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Certainly do not want to update every time the table renders.
  }, [data]);

  useEffect(() => {
    meta && ctx.setMeta?.(meta);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Certainly do not want to update every time the table renders.
  }, [meta]);

  useEffect(() => {
    columns && ctx.setColumns?.(columns);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Certainly do not want to update every time the table renders.
  }, [columns]);

  useEffect(() => {
    // Not sure if we'll have a scenario where export options could be falsey and we wouldn't want to change it, undefined is probably more explicit than false.
    if (exportOptions === undefined) ctx.setExportOptions(undefined);
    if (!exportOptions) return;
    ctx.setExportOptions(exportOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Certainly do not want to update every time the table renders.
  }, [exportOptions]);

  useEffect(() => {
    if (isLoading === undefined) return;
    ctx.setIsLoading(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Don't update every time the table renders.
  }, [isLoading]);

  useEffect(() => {
    if (!pdfOptions) return;
    if (pdfOptions !== ctx.pdfOptions) {
      ctx.setPdfOptions(pdfOptions);
    }
    // Clear any pdfOptions when the component unmounts.
    return () => {
      ctx.setPdfOptions(undefined);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfOptions]);

  return (
    <>
      {debug ? (
        <>
          <TableDebugger />
        </>
      ) : null}
    </>
  );
};

export { TableDataProviderComponent };
